/**
 * 全局WebSocket服务
 * 参考 vue-simple-websocket 的设计理念，提供应用级别的WebSocket连接管理
 * 支持消息分发机制，让多个组件可以共享同一个WebSocket连接
 */
import { createWebSocketUrl } from '../api2.js'

class WebSocketService {
  constructor() {
    this.websocket = null
    this.isConnected = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectTimer = null
    this.isManualClose = false

    // 心跳机制
    this.heartbeatTimer = null
    this.heartbeatInterval = 20000 // 20秒
    this.latestPingTime = 0
    this.connectionLatency = 0
    this.missedHeartbeats = 0
    this.maxMissedHeartbeats = 3
    this.connectionQuality = 'unknown'

    // 🚀 新增：消息分发机制
    this.subscribers = new Map() // 存储订阅者的回调函数

    // 🚀 新增：重要消息缓存机制
    this.importantMessageCache = new Map() // 缓存重要消息（如user_info）
    this.importantMessageTypes = new Set(['user_info', 'connection_status']) // 需要缓存的消息类型

    // 🚀 新增：缓存消息时间戳，用于避免将历史缓存当作新消息
    this.cacheTimestamps = new Map() // 记录每个缓存消息的时间戳

    // 传统事件处理器（保持向后兼容）
    this.onOpen = null
    this.onMessage = null
    this.onClose = null
    this.onError = null
    this.onConnectionStatusChange = null

    // 消息队列（连接断开时暂存消息）
    this.messageQueue = []
    // 🚀 修复：添加消息去重和时间戳管理
    this.sentMessageIds = new Set() // 已发送消息ID集合
    this.messageTimeout = 30000 // 消息超时时间30秒
    this.lastCleanupTime = Date.now()

    // 🚀 新增：Ticket验证缓存
    this.ticketValidationCache = new Map() // 缓存ticket验证结果

    console.log('🔌 WebSocket服务已初始化，支持消息分发机制')
  }

  /**
   * 🚀 订阅消息（新增）
   * @param {string} subscriberId - 订阅者ID（通常是组件名称）
   * @param {function} callback - 消息回调函数
   * @param {object} options - 订阅选项
   * @param {boolean} options.skipCachedMessages - 是否跳过缓存消息（默认false）
   * @param {boolean} options.skipConnectionStatus - 是否跳过连接状态通知（默认false）
   */
  subscribe(subscriberId, callback, options = {}) {
    if (typeof callback !== 'function') {
      console.error('❌ 订阅失败：回调函数无效')
      return false
    }

    const { skipCachedMessages = false, skipConnectionStatus = false } = options

    this.subscribers.set(subscriberId, callback)
    console.log(`✅ 组件 ${subscriberId} 已订阅WebSocket消息`, options)

    // 🚀 根据选项决定是否发送缓存的重要消息给新订阅者
    if (!skipCachedMessages) {
      this.importantMessageCache.forEach((message, messageType) => {
        try {
          console.log(`📨 向新订阅者 ${subscriberId} 发送缓存消息:`, messageType)
          callback(message)
        } catch (error) {
          console.error(`❌ 向订阅者 ${subscriberId} 发送缓存消息失败:`, error)
        }
      })
    } else {
      console.log(`⏭️ 跳过向订阅者 ${subscriberId} 发送缓存消息`)
    }

    // 如果已经连接，根据选项决定是否通知订阅者连接状态
    if (this.isConnected && !skipConnectionStatus) {
      callback({
        type: 'connection_status',
        status: 'connected',
        connectionInfo: this.getStatus(),
      })
    }

    return true
  }

  /**
   * 🚀 取消订阅（新增）
   * @param {string} subscriberId - 订阅者ID
   */
  unsubscribe(subscriberId) {
    const removed = this.subscribers.delete(subscriberId)
    if (removed) {
      console.log(`✅ 组件 ${subscriberId} 已取消订阅WebSocket消息`)
    }
    return removed
  }

  /**
   * 🚀 清除过期的缓存消息
   * @param {number} maxAge - 最大缓存时间（毫秒），默认5分钟
   */
  clearExpiredCache(maxAge = 5 * 60 * 1000) {
    const now = Date.now()
    const expiredTypes = []

    this.cacheTimestamps.forEach((timestamp, messageType) => {
      if (now - timestamp > maxAge) {
        expiredTypes.push(messageType)
      }
    })

    expiredTypes.forEach((messageType) => {
      this.importantMessageCache.delete(messageType)
      this.cacheTimestamps.delete(messageType)
      console.log(`🗑️ 清除过期缓存消息: ${messageType}`)
    })

    return expiredTypes.length
  }

  /**
   * 🚀 清除所有缓存消息
   */
  clearAllCache() {
    const cacheCount = this.importantMessageCache.size
    this.importantMessageCache.clear()
    this.cacheTimestamps.clear()
    console.log(`🗑️ 已清除所有缓存消息 (${cacheCount}条)`)
    return cacheCount
  }

  /**
   * 🚀 获取缓存状态信息
   */
  getCacheStatus() {
    const now = Date.now()
    const cacheInfo = []

    this.importantMessageCache.forEach((message, messageType) => {
      const timestamp = this.cacheTimestamps.get(messageType)
      const age = timestamp ? now - timestamp : 0
      cacheInfo.push({
        type: messageType,
        age: age,
        ageFormatted: `${Math.round(age / 1000)}秒前`,
        message: message,
      })
    })

    return {
      totalCached: this.importantMessageCache.size,
      cacheInfo: cacheInfo,
    }
  }

  /**
   * 🚀 处理聊天消息通知（新增）
   * @param {object} message - 收到的消息
   */
  async handleChatMessageNotification(message) {
    // 动态加载通知管理器，避免循环依赖
    try {
      const { default: notificationManager } = await import('./notification')

      // 识别聊天消息（根据实际的消息格式调整）
      console.log('🔍 检查是否为聊天消息:', message)
      if (this.isChatMessage(message)) {
        console.log('✅ 识别到聊天消息，开始解析:', message.type || message.Action)
        const chatMessage = this.parseChatMessage(message)
        if (chatMessage) {
          console.log('📨 向通知管理器发送聊天消息:', chatMessage)
          notificationManager.handleNewMessage(chatMessage)
        } else {
          console.warn('⚠️ 聊天消息解析失败:', message)
        }
      } else {
        console.log('🔍 非聊天消息，跳过通知处理:', message.type || message.Action)
      }
    } catch (error) {
      console.warn('⚠️ 无法加载通知管理器:', error.message)
      console.warn('⚠️ 错误详情:', error)
    }
  }

  /**
   * 🚀 判断是否为聊天消息（新增）
   * @param {object} message - 消息对象
   * @returns {boolean}
   */
  isChatMessage(message) {
    // 根据实际的消息格式来判断
    // 🚀 修复：添加更多消息类型的支持，确保张三的消息能被识别
    // 🚀 重要修复：排除conversation_update类型，避免会话更新被当作新消息处理
    return (
      message.Action === 'ReceiveMessage' ||
      message.type === 'chat_message' ||
      message.type === 'message' ||
      // message.type === 'conversation_update' || // 🚀 移除：会话更新不应该被当作聊天消息
      message.type === 'new_message' || // 新增：支持new_message类型
      message.type === 'message_notification' || // 🚀 新增：支持message_notification类型
      message.action === 'new_message' || // 新增：支持action字段
      (message.from_user_id &&
        message.notification_data &&
        message.type !== 'conversation_update') || // 🚀 新增：支持通知类型消息，但排除会话更新
      (message.from_user_id && message.content && message.type !== 'conversation_update') || // 新增：直接包含发送者和内容的消息，但排除会话更新
      (message.Data && typeof message.Data === 'string' && message.Data.includes('message'))
    )
  }

  /**
   * 🚀 解析聊天消息（新增）
   * @param {object} message - 原始消息对象
   * @returns {object|null} - 解析后的聊天消息对象
   */
  parseChatMessage(message) {
    try {
      // 🚀 安全检查：确保不处理conversation_update类型的消息
      if (message.type === 'conversation_update') {
        console.warn('⚠️ conversation_update消息不应该被解析为聊天消息，跳过处理')
        return null
      }

      let fromUserId, fromUserName, content, timestamp

      console.log('🔍 开始解析聊天消息:', JSON.stringify(message, null, 2))

      // 根据实际的消息格式进行解析
      if (message.Action === 'ReceiveMessage' && message.Data) {
        const data = typeof message.Data === 'string' ? JSON.parse(message.Data) : message.Data
        fromUserId = data.from_user_id || data.fromUserId || data.sender_id
        fromUserName =
          data.from_user_name || data.fromUserName || data.sender_name || data.from_nickname
        content = data.content || data.message || data.text
        timestamp = data.timestamp || data.time || Date.now()
      } else if (
        message.type === 'chat_message' ||
        message.type === 'message' ||
        message.type === 'new_message'
      ) {
        fromUserId = message.from_user_id || message.fromUserId || message.sender_id
        fromUserName =
          message.from_user_name ||
          message.fromUserName ||
          message.sender_name ||
          message.from_nickname
        content = message.content || message.message || message.text
        timestamp = message.timestamp || message.time || Date.now()
        // } else if (message.type === 'conversation_update') {
        //   // 🚀 移除：conversation_update不应该被当作聊天消息处理
        //   // 这类消息应该由IMChat组件直接处理，而不是通过通知管理器
        //   fromUserId = message.from_user_id
        //   fromUserName =
        //     message.from_user_name || message.from_nickname || `用户${message.from_user_id}`
        //   content = message.content || message.last_message || '新消息'
        //   timestamp = message.timestamp ? message.timestamp * 1000 : Date.now()
        //   console.log('📧 跳过conversation_update消息处理')
        // }
      } else if (message.type === 'message_notification') {
        // 🚀 新增：支持message_notification类型的消息
        fromUserId = message.from_user_id
        fromUserName =
          message.from_user_name || message.from_nickname || `用户${message.from_user_id}`
        // 优先使用notification_data中的body作为内容
        content =
          (message.notification_data && message.notification_data.body) ||
          message.content ||
          '新消息'
        timestamp = message.timestamp ? message.timestamp * 1000 : Date.now()

        console.log('📧 解析message_notification消息:', {
          fromUserId,
          fromUserName,
          content,
          timestamp,
        })
      } else if (message.action === 'new_message' || (message.from_user_id && message.content)) {
        // 🚀 新增：支持直接格式的消息（张三发送的消息可能是这种格式）
        fromUserId = message.from_user_id || message.fromUserId || message.sender_id
        fromUserName =
          message.from_user_name ||
          message.fromUserName ||
          message.sender_name ||
          message.from_nickname ||
          '张三'
        content = message.content || message.message || message.text
        timestamp = message.timestamp || message.time || Date.now()

        console.log('📧 解析直接格式消息:', { fromUserId, fromUserName, content, timestamp })
      }

      // 验证必要字段
      if (fromUserId !== undefined && fromUserId !== null) {
        const parsedMessage = {
          fromUserId: String(fromUserId),
          fromUserName: fromUserName || `用户${fromUserId}`,
          content: String(content || '新消息'),
          timestamp: Number(timestamp) || Date.now(),
        }

        console.log('✅ 成功解析聊天消息:', parsedMessage)
        return parsedMessage
      }

      console.warn('⚠️ 解析失败：缺少必要字段 fromUserId')
      return null
    } catch (error) {
      console.error('❌ 解析聊天消息失败:', error)
      return null
    }
  }

  /**
   * 分发消息给所有订阅者（简化版本）
   * @param {object} message - 要分发的消息
   */
  dispatchMessage(message) {
    // 缓存重要消息并记录时间戳
    if (this.importantMessageTypes.has(message.type)) {
      this.importantMessageCache.set(message.type, message)
      this.cacheTimestamps.set(message.type, Date.now())
      console.log(`💾 缓存重要消息: ${message.type}`)
    }

    if (this.subscribers.size === 0) {
      return
    }

    console.log(`📢 分发消息给 ${this.subscribers.size} 个订阅者:`, message.type)

    // 分发给所有订阅者
    this.subscribers.forEach((callback, subscriberId) => {
      try {
        callback(message)
      } catch (error) {
        console.error(`❌ 订阅者 ${subscriberId} 处理消息失败:`, error)
      }
    })
  }

  /**
   * 连接WebSocket
   */
  async connect() {
    const ticket = window.localStorage.getItem('ticket')
    if (!ticket) {
      console.warn('❌ 无法连接WebSocket：未找到认证票据')
      this.emitStatusChange('error', '认证票据不存在，请重新登录')
      return false
    }

    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      console.log('🔌 WebSocket已连接，跳过重复连接')
      return true
    }

    if (this.websocket && this.websocket.readyState === WebSocket.CONNECTING) {
      console.log('🔌 WebSocket正在连接中，等待连接完成')
      return true
    }

    // 清理现有连接
    this.cleanup()

    // 🔍 连接前的网络诊断
    await this.performNetworkDiagnostics()

    // 🚀 修复：使用封装的API配置构建WebSocket URL
    const wsUrl = createWebSocketUrl('/ws', {
      authorization: `Bearer ${ticket}`,
    }).replace('ws://', 'wss://') // 确保使用安全连接

    // 🔍 详细调试信息
    console.log('🔗 WebSocket连接详情:')
    console.log('  - URL:', wsUrl)
    console.log('  - Ticket长度:', ticket.length)
    console.log('  - Ticket前缀:', ticket.substring(0, 20) + '...')
    console.log('  - 重连次数:', this.reconnectAttempts)
    console.log('  - 当前时间:', new Date().toISOString())

    try {
      this.websocket = new WebSocket(wsUrl)
      this.setupEventHandlers()
      return true
    } catch (error) {
      console.error('❌ WebSocket创建失败:', error)
      this.emitStatusChange('error', 'WebSocket创建失败')
      this.scheduleReconnect()
      return false
    }
  }

  /**
   * 🚀 验证Ticket有效性
   * @param {string} ticket - 用户认证票据
   * @returns {Promise<boolean>} - 返回一个Promise，解析为是否允许重连
   */
  async validateTicket(ticket) {
    if (!ticket) {
      return false
    }

    // 检查缓存
    if (this.ticketValidationCache.has(ticket)) {
      const cachedResult = this.ticketValidationCache.get(ticket)
      console.log('✅ 使用缓存的Ticket验证结果:', cachedResult)
      return cachedResult.allowReconnect
    }

    try {
      // 🚀 修复：使用封装的API配置构建URL
      const validationUrl = createWebSocketUrl('/api/validate-ticket', { ticket }).replace(
        /^ws/,
        'http',
      )

      console.log('🔐 正在验证Ticket:', validationUrl)

      const response = await fetch(validationUrl)
      const data = await response.json()

      if (response.ok && data.code === 200) {
        const allowReconnect = data.cnmsg === true
        console.log(`✅ Ticket验证成功: ${data.data}, 是否允许重连: ${allowReconnect}`)

        // 缓存结果
        this.ticketValidationCache.set(ticket, {
          allowReconnect,
          username: data.data,
          timestamp: Date.now(),
        })
        return allowReconnect
      } else {
        console.error('❌ Ticket验证失败:', data.msg || '未知错误')
        // 缓存失败结果，防止频繁重试
        this.ticketValidationCache.set(ticket, {
          allowReconnect: false,
          error: data.msg,
          timestamp: Date.now(),
        })
        return false
      }
    } catch (error) {
      console.error('❌ Ticket验证请求异常:', error)
      return false // 网络异常时，暂时禁止重连
    }
  }

  /**
   * 🚀 清除Ticket验证缓存
   */
  clearTicketValidationCache() {
    this.ticketValidationCache.clear()
    console.log('🗑️ 已清除所有Ticket验证缓存')
  }

  /**
   * 设置WebSocket事件处理器
   */
  setupEventHandlers() {
    if (!this.websocket) return

    // 连接超时处理
    const connectionTimeout = setTimeout(() => {
      if (this.websocket && this.websocket.readyState === WebSocket.CONNECTING) {
        console.error('❌ WebSocket连接超时')
        console.error('  - 超时时间: 15秒')
        console.error('  - 当前状态:', this.websocket.readyState)
        console.error('  - 可能原因: 网络慢、服务器响应慢、防火墙阻止')
        this.websocket.close()
        this.emitStatusChange('error', '连接超时，请检查网络')
        this.scheduleReconnect()
      }
    }, 15000)

    // 🔍 连接状态监控
    this.monitorConnectionState()

    this.websocket.onopen = (event) => {
      clearTimeout(connectionTimeout)

      // 🔍 详细的连接成功信息
      console.log('✅ WebSocket连接成功详情:')
      console.log('  - 连接URL:', this.websocket.url)
      console.log('  - 协议:', this.websocket.protocol || '默认')
      console.log('  - 就绪状态:', this.websocket.readyState)
      console.log('  - 扩展:', this.websocket.extensions || '无')
      console.log('  - 连接时间:', new Date().toISOString())
      console.log('  - 重连次数:', this.reconnectAttempts)

      this.isConnected = true
      this.reconnectAttempts = 0
      this.missedHeartbeats = 0
      this.connectionQuality = 'good'

      // 🚀 清理过期的缓存消息
      this.clearExpiredCache()

      // 启动心跳
      this.startHeartbeat()

      // 发送队列中的消息
      this.flushMessageQueue()

      // 🚀 新增：通知所有订阅者连接已建立
      this.dispatchMessage({
        type: 'connection_status',
        status: 'connected',
        connectionInfo: this.getStatus(),
      })

      // 触发事件
      this.emitStatusChange('success', '连接成功')
      if (this.onOpen) this.onOpen(event)
    }

    this.websocket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)
        console.log('📥 收到WebSocket消息:', message)

        // 处理pong响应
        if (message.type === 'pong') {
          this.handlePongResponse()
        }

        // 🚀 处理聊天消息通知
        this.handleChatMessageNotification(message)

        // 分发消息给所有订阅者
        this.dispatchMessage(message)

        // 保持向后兼容：触发传统消息事件
        if (this.onMessage) this.onMessage(message, event)
      } catch (error) {
        console.error('❌ 解析WebSocket消息失败:', error)
      }
    }

    this.websocket.onclose = (event) => {
      clearTimeout(connectionTimeout)

      // 🔍 详细的关闭信息调试
      console.log('🔌 WebSocket连接关闭详情:')
      console.log('  - 关闭代码:', event.code)
      console.log('  - 关闭原因:', event.reason || '无原因')
      console.log('  - 是否干净关闭:', event.wasClean)
      console.log('  - 连接持续时间:', this.websocket ? 'N/A' : '未知')
      console.log('  - 是否手动关闭:', this.isManualClose)
      console.log('  - 重连次数:', this.reconnectAttempts)

      this.isConnected = false
      this.clearHeartbeat()

      let closeReason = this.getCloseReason(event.code)

      // 🚀 新增：通知所有订阅者连接已断开
      this.dispatchMessage({
        type: 'connection_status',
        status: 'disconnected',
        code: event.code,
        reason: closeReason,
        connectionInfo: this.getStatus(),
      })

      if (event.code === 1006) {
        console.error('❌ 连接异常断开 - 可能的原因:')
        console.error('  1. 网络连接问题')
        console.error('  2. 服务器主动断开连接')
        console.error('  3. 认证失败')
        console.error('  4. 防火墙或代理问题')
        this.emitStatusChange('error', '连接异常断开')
      } else if (event.code === 1000) {
        this.emitStatusChange('info', '连接正常关闭')
      } else if (event.code === 1002) {
        console.error('❌ 协议错误 - 可能的原因:')
        console.error('  1. WebSocket URL格式错误')
        console.error('  2. 服务器不支持WebSocket协议')
        console.error('  3. 认证参数格式错误')
        this.emitStatusChange('error', `连接已断开: ${closeReason}`)
      } else if (event.code === 1008) {
        console.error('❌ 策略违规 - 可能的原因:')
        console.error('  1. 认证token无效或过期')
        console.error('  2. 用户权限不足')
        console.error('  3. 服务器拒绝连接')
        this.emitStatusChange('error', `连接已断开: ${closeReason}`)
      } else {
        this.emitStatusChange('error', `连接已断开: ${closeReason}`)
      }

      // 触发关闭事件
      if (this.onClose) this.onClose(event)

      // 如果不是手动关闭，尝试重连
      if (!this.isManualClose) {
        this.scheduleReconnect()
      }
    }

    this.websocket.onerror = (error) => {
      clearTimeout(connectionTimeout)

      // 🔍 详细的错误信息调试
      console.error('❌ WebSocket连接错误详情:')
      console.error('  - 错误对象:', error)
      console.error('  - 错误类型:', error.type || '未知')
      console.error('  - 错误消息:', error.message || '无消息')
      console.error('  - 当前状态:', this.websocket ? this.websocket.readyState : '未知')
      console.error('  - 连接URL:', this.websocket ? this.websocket.url : '未知')
      console.error('  - 发生时间:', new Date().toISOString())
      console.error('  - 重连次数:', this.reconnectAttempts)

      // 🔍 常见错误原因分析
      console.error('❌ 可能的错误原因:')
      console.error('  1. 网络连接问题（检查网络连接）')
      console.error('  2. 服务器地址错误或服务器未启动')
      console.error('  3. 认证参数错误（检查ticket有效性）')
      console.error('  4. 防火墙或代理阻止连接')
      console.error('  5. SSL/TLS证书问题（wss连接）')
      console.error('  6. 服务器拒绝WebSocket升级请求')

      this.emitStatusChange('error', '连接出错')
      if (this.onError) this.onError(error)
    }
  }

  /**
   * 发送消息
   */
  send(data) {
    // 🚀 修复：添加消息去重和时间戳检查
    const messageWithMeta = this.addMessageMetadata(data)

    if (!this.isConnected || !this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ WebSocket未连接，消息已加入队列')
      // 检查消息是否已在队列中
      if (!this.isDuplicateMessage(messageWithMeta)) {
        this.messageQueue.push(messageWithMeta)
        console.log('📝 消息已加入队列，队列长度:', this.messageQueue.length)
      } else {
        console.log('🔄 消息已存在于队列中，跳过重复添加')
      }
      return false
    }

    try {
      // 检查是否为重复消息
      if (this.isDuplicateSentMessage(messageWithMeta)) {
        console.log('🔄 消息已发送过，跳过重复发送')
        return true
      }

      const message =
        typeof messageWithMeta.data === 'object'
          ? JSON.stringify(messageWithMeta.data)
          : messageWithMeta.data
      this.websocket.send(message)

      // 记录已发送的消息
      this.recordSentMessage(messageWithMeta)

      console.log('📤 发送WebSocket消息:', messageWithMeta.data)
      return true
    } catch (error) {
      console.error('❌ 发送WebSocket消息失败:', error)
      if (!this.isDuplicateMessage(messageWithMeta)) {
        this.messageQueue.push(messageWithMeta)
      }
      return false
    }
  }

  /**
   * 发送对象消息（便捷方法）
   */
  sendObj(obj) {
    return this.send(obj)
  }

  /**
   * 断开连接
   */
  disconnect() {
    this.isManualClose = true
    this.cleanup()
    console.log('🔌 WebSocket已手动断开')
  }

  /**
   * 清理资源
   */
  cleanup() {
    this.clearHeartbeat()
    this.clearReconnectTimer()

    // 🚀 清除过期的缓存消息
    this.clearExpiredCache()

    if (this.websocket) {
      this.websocket.onopen = null
      this.websocket.onmessage = null
      this.websocket.onclose = null
      this.websocket.onerror = null

      if (
        this.websocket.readyState === WebSocket.OPEN ||
        this.websocket.readyState === WebSocket.CONNECTING
      ) {
        this.websocket.close()
      }

      this.websocket = null
    }

    this.isConnected = false
  }

  /**
   * 重连调度
   */
  async scheduleReconnect() {
    // 🚀 在重连前验证Ticket
    const ticket = window.localStorage.getItem('ticket')
    const allowReconnect = await this.validateTicket(ticket)

    if (!allowReconnect) {
      console.error('❌ Ticket无效或已过期，不允许重连。请重新登录。')
      this.emitStatusChange('error', '登录状态已过期，请重新登录')
      this.isManualClose = true // 标记为手动关闭，防止意外的重连逻辑
      this.disconnect()
      return
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ 达到最大重连次数，停止重连')
      this.emitStatusChange('error', '重连失败，请刷新页面重试')
      return
    }

    this.reconnectAttempts++
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000)

    console.log(`🔄 计划在 ${delay / 1000} 秒后进行第 ${this.reconnectAttempts} 次重连...`)
    this.emitStatusChange(
      'info',
      `正在重连... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
    )

    this.clearReconnectTimer()
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, delay)
  }

  /**
   * 心跳机制
   */
  startHeartbeat() {
    this.clearHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.latestPingTime = Date.now()
        this.send({ type: 'ping' })
      } else {
        this.missedHeartbeats++
        if (this.missedHeartbeats >= this.maxMissedHeartbeats) {
          console.warn('💔 心跳丢失次数过多，强制重连')
          this.cleanup()
          this.scheduleReconnect()
        }
      }
    }, this.heartbeatInterval)
  }

  clearHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  /**
   * 处理pong响应
   */
  handlePongResponse() {
    const currentTime = Date.now()
    this.connectionLatency = currentTime - this.latestPingTime
    this.missedHeartbeats = 0

    console.log(`💗 收到pong响应，延迟: ${this.connectionLatency}ms`)

    // 更新连接质量
    if (this.connectionLatency < 100) {
      this.connectionQuality = 'good'
    } else if (this.connectionLatency < 1000) {
      this.connectionQuality = 'poor'
    } else {
      this.connectionQuality = 'critical'
    }
  }

  /**
   * 发送队列中的消息
   */
  flushMessageQueue() {
    if (this.messageQueue.length > 0) {
      console.log(`📨 发送队列中的 ${this.messageQueue.length} 条消息`)

      // 🚀 修复：过滤过期和重复消息
      const validMessages = this.filterValidMessages(this.messageQueue)
      console.log(`📋 过滤后有效消息: ${validMessages.length} 条`)

      this.messageQueue = []

      validMessages.forEach((messageWithMeta) => {
        // 直接发送，不再调用send方法避免重复检查
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
          try {
            if (!this.isDuplicateSentMessage(messageWithMeta)) {
              const message =
                typeof messageWithMeta.data === 'object'
                  ? JSON.stringify(messageWithMeta.data)
                  : messageWithMeta.data
              this.websocket.send(message)
              this.recordSentMessage(messageWithMeta)
              console.log('📤 队列消息发送成功:', messageWithMeta.data)
            } else {
              console.log('🔄 队列中的消息已发送过，跳过')
            }
          } catch (error) {
            console.error('❌ 队列消息发送失败:', error)
          }
        }
      })
    }
  }

  /**
   * 获取关闭代码说明
   */
  getCloseReason(code) {
    const reasons = {
      1000: '正常关闭',
      1001: '终端离开',
      1002: '协议错误',
      1003: '不支持的数据',
      1004: '保留',
      1005: '未收到状态码',
      1006: '连接异常关闭',
      1007: '数据格式错误',
      1008: '策略违规',
      1009: '消息过大',
      1010: '扩展协商失败',
      1011: '服务器错误',
      1015: 'TLS握手失败',
    }
    return reasons[code] || `未知错误 (${code})`
  }

  /**
   * 触发状态变化事件
   */
  emitStatusChange(type, message) {
    console.log(`[${type.toUpperCase()}] ${message}`)
    if (this.onConnectionStatusChange) {
      this.onConnectionStatusChange(type, message, {
        isConnected: this.isConnected,
        connectionLatency: this.connectionLatency,
        connectionQuality: this.connectionQuality,
        reconnectAttempts: this.reconnectAttempts,
      })
    }
  }

  /**
   * 🚀 新增：添加消息元数据
   */
  addMessageMetadata(data) {
    const timestamp = Date.now()
    const messageId = this.generateMessageId(data, timestamp)
    return {
      id: messageId,
      data: data,
      timestamp: timestamp,
    }
  }

  /**
   * 🚀 新增：生成消息ID
   */
  generateMessageId(data, timestamp) {
    const dataStr = typeof data === 'object' ? JSON.stringify(data) : String(data)
    return `${timestamp}_${this.hashCode(dataStr)}`
  }

  /**
   * 🚀 新增：简单哈希函数
   */
  hashCode(str) {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // 转换为32位整数
    }
    return Math.abs(hash)
  }

  /**
   * 🚀 新增：检查是否为重复消息（队列中）
   */
  isDuplicateMessage(messageWithMeta) {
    return this.messageQueue.some(
      (queuedMessage) =>
        queuedMessage.id === messageWithMeta.id ||
        (JSON.stringify(queuedMessage.data) === JSON.stringify(messageWithMeta.data) &&
          Math.abs(queuedMessage.timestamp - messageWithMeta.timestamp) < 1000),
    )
  }

  /**
   * 🚀 新增：检查是否为重复发送的消息
   */
  isDuplicateSentMessage(messageWithMeta) {
    // 定期清理过期的消息ID
    this.cleanupExpiredMessageIds()
    return this.sentMessageIds.has(messageWithMeta.id)
  }

  /**
   * 🚀 新增：记录已发送的消息
   */
  recordSentMessage(messageWithMeta) {
    this.sentMessageIds.add(messageWithMeta.id)
  }

  /**
   * 🚀 新增：清理过期的消息ID
   */
  cleanupExpiredMessageIds() {
    const now = Date.now()
    if (now - this.lastCleanupTime > this.messageTimeout) {
      // 简单清理：如果集合过大，清空重新开始
      if (this.sentMessageIds.size > 1000) {
        this.sentMessageIds.clear()
        console.log('🧹 清理过期消息ID缓存')
      }
      this.lastCleanupTime = now
    }
  }

  /**
   * 🚀 新增：过滤有效消息
   */
  filterValidMessages(messages) {
    const now = Date.now()
    const validMessages = []
    const seenIds = new Set()

    // 按时间戳排序，优先处理较新的消息
    const sortedMessages = messages.sort((a, b) => b.timestamp - a.timestamp)

    for (const message of sortedMessages) {
      // 检查消息是否过期
      if (now - message.timestamp > this.messageTimeout) {
        console.log('⏰ 消息已过期，跳过:', message.data)
        continue
      }

      // 检查是否重复
      if (seenIds.has(message.id)) {
        console.log('🔄 发现重复消息，跳过:', message.data)
        continue
      }

      seenIds.add(message.id)
      validMessages.push(message)
    }

    return validMessages
  }

  /**
   * 🔍 连接状态监控方法
   */
  monitorConnectionState() {
    if (!this.websocket) return

    const startTime = Date.now()
    console.log('🔍 开始监控WebSocket连接状态...')

    const monitor = setInterval(() => {
      if (!this.websocket) {
        clearInterval(monitor)
        return
      }

      const currentState = this.websocket.readyState
      const elapsed = Date.now() - startTime

      console.log(`📊 连接状态监控 (${elapsed}ms):`, {
        状态: this.getReadyStateText(currentState),
        状态码: currentState,
        URL: this.websocket.url,
        协议: this.websocket.protocol || '默认',
      })

      // 如果连接成功或失败，停止监控
      if (currentState === WebSocket.OPEN || currentState === WebSocket.CLOSED) {
        clearInterval(monitor)
        console.log('🔍 连接状态监控结束')
      }

      // 超过30秒仍在连接中，记录警告
      if (elapsed > 30000 && currentState === WebSocket.CONNECTING) {
        console.warn('⚠️ WebSocket连接时间过长，可能存在问题')
        clearInterval(monitor)
      }
    }, 1000) // 每秒检查一次
  }

  /**
   * 🔍 获取WebSocket状态文本
   */
  getReadyStateText(state) {
    const states = {
      [WebSocket.CONNECTING]: '连接中',
      [WebSocket.OPEN]: '已连接',
      [WebSocket.CLOSING]: '关闭中',
      [WebSocket.CLOSED]: '已关闭',
    }
    return states[state] || '未知状态'
  }

  /**
   * 🔍 网络诊断方法
   */
  async performNetworkDiagnostics() {
    console.log('🔍 开始网络诊断...')

    // 1. 检查网络连接状态
    const isOnline = navigator.onLine
    console.log('  - 网络状态:', isOnline ? '在线' : '离线')

    if (!isOnline) {
      console.error('❌ 网络离线，无法建立WebSocket连接')
      return
    }

    // 2. 测试基础HTTP连接
    try {
      const startTime = Date.now()
      const response = await fetch('https://hjm.xyhy.live/health', {
        //https://hjm.xyhy.live
        method: 'GET',
        timeout: 5000,
      })
      const latency = Date.now() - startTime
      console.log('  - HTTP连接测试:', response.ok ? '成功' : '失败')
      console.log('  - 服务器延迟:', latency + 'ms')

      if (!response.ok) {
        console.warn('⚠️ HTTP连接测试失败，状态码:', response.status)
      }
    } catch (error) {
      console.error('❌ HTTP连接测试失败:', error.message)
      console.error('  可能原因: 服务器不可达、DNS解析失败、网络超时')
    }

    // 3. 检查WebSocket支持
    if (typeof WebSocket === 'undefined') {
      console.error('❌ 浏览器不支持WebSocket')
      return
    }
    console.log('  - WebSocket支持:', '是')

    // 4. 检查认证票据有效性
    const ticket = window.localStorage.getItem('ticket')
    if (ticket) {
      try {
        // 简单的票据格式检查
        const isValidFormat = ticket.length > 10 && !ticket.includes(' ')
        console.log('  - Ticket格式:', isValidFormat ? '有效' : '可能无效')

        // 检查票据是否过期（如果有时间戳信息）
        if (ticket.includes('.')) {
          console.log('  - Ticket类型: JWT格式')
        } else {
          console.log('  - Ticket类型: 普通字符串')
        }
      } catch (error) {
        console.warn('⚠️ Ticket检查失败:', error.message)
      }
    }

    console.log('🔍 网络诊断完成')
  }

  /**
   * 获取连接状态
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      connectionLatency: this.connectionLatency,
      connectionQuality: this.connectionQuality,
      reconnectAttempts: this.reconnectAttempts,
      readyState: this.websocket ? this.websocket.readyState : null,
    }
  }
}

// 创建全局实例
const websocketService = new WebSocketService()

// 🚀 创建全局调试接口
if (typeof window !== 'undefined') {
  window.debugWebSocket = {
    // 缓存管理
    clearCache: () => {
      const count = websocketService.clearAllCache()
      console.log(`🗑️ 已清除 ${count} 条缓存消息`)
      return count
    },

    clearExpiredCache: (maxAge) => {
      const count = websocketService.clearExpiredCache(maxAge)
      console.log(`🗑️ 已清除 ${count} 条过期缓存消息`)
      return count
    },

    getCacheStatus: () => {
      const status = websocketService.getCacheStatus()
      console.table(status.cacheInfo)
      return status
    },

    // 订阅管理
    getSubscribers: () => {
      const subscribers = Array.from(websocketService.subscribers.keys())
      console.log('📋 当前订阅者:', subscribers)
      return subscribers
    },

    // 连接状态
    getStatus: () => {
      const status = websocketService.getStatus()
      console.table(status)
      return status
    },

    // 重连
    reconnect: () => {
      console.log('🔄 手动重连WebSocket...')
      websocketService.disconnect()
      setTimeout(() => websocketService.connect(), 1000)
    },

    // 帮助信息
    help: () => {
      console.log(`
🔧 WebSocket调试命令:

缓存管理:
  - debugWebSocket.clearCache()           清除所有缓存消息
  - debugWebSocket.clearExpiredCache()    清除过期缓存消息
  - debugWebSocket.getCacheStatus()       查看缓存状态

订阅管理:
  - debugWebSocket.getSubscribers()       查看当前订阅者

连接管理:
  - debugWebSocket.getStatus()            查看连接状态
  - debugWebSocket.reconnect()            手动重连

通知管理:
  - debugNotifications.reset()            重置通知状态（清除红点）
  - debugNotifications.getStatus()        查看通知状态

帮助:
  - debugWebSocket.help()                 显示此帮助信息
`)
    },
  }

  // 输出调试接口可用提示
  console.log('🔧 WebSocket调试接口已加载，使用 debugWebSocket.help() 查看可用命令')
}

export default websocketService
